import { useEffect, useState } from 'react';
import { Badge, Button, Modal } from '../../components/primitives';
import { ListPage, ListTable, type ListColumn } from '../../components/list';
import { DocBlock, DocRow, DocInlineDate } from '../../components/document';
import { StavbaGate } from '../../components/stavba';
import { useActiveStavba } from '../stavba-context/StavbaContext';
import {
  useZmenoveListy,
  useCreateZmenovyList,
  useZmenovyListDetail,
  useUpdateZmenovyList,
  useDeleteZmenovyList,
} from '../../api/hooks';
import type { ZmenovyList, ZmenovyListStav } from '../../api/schemas';

const stavMap: Record<string, { tone: 'yellow' | 'success' | 'danger' | 'neutral'; label: string }> = {
  navrh: { tone: 'yellow', label: 'Návrh' },
  ke_schvaleni: { tone: 'yellow', label: 'Ke schválení' },
  schvaleno: { tone: 'success', label: 'Schváleno' },
  zamitnuto: { tone: 'danger', label: 'Zamítnuto' },
};

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('cs-CZ');
}

function czToIso(s: string): string | null {
  const m = s.trim().match(/^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})$/);
  if (!m) return null;
  return `${m[3]}-${String(m[2]).padStart(2, '0')}-${String(m[1]).padStart(2, '0')}`;
}

function isoToCz(iso?: string | null): string {
  if (!iso) return '';
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return `${parseInt(m[3], 10)}. ${parseInt(m[2], 10)}. ${m[1]}`;
}

export function ZmenyPage() {
  const { activeStavbaId } = useActiveStavba();
  const { data: rows = [], isLoading, error } = useZmenoveListy(activeStavbaId);
  const create = useCreateZmenovyList(activeStavbaId);
  const update = useUpdateZmenovyList(activeStavbaId);
  const del = useDeleteZmenovyList(activeStavbaId);

  const [openNew, setOpenNew] = useState(false);
  const [popisZmeny, setPopisZmeny] = useState('');
  const [duvod, setDuvod] = useState('');
  const [castka, setCastka] = useState('');
  const [dopadNaklady, setDopadNaklady] = useState('');
  const [dopadHarmonogram, setDopadHarmonogram] = useState('');
  const [puvodniRozsah, setPuvodniRozsah] = useState('');
  const [schvalil, setSchvalil] = useState('');
  const [datumSchvaleni, setDatumSchvaleni] = useState('');

  const [editId, setEditId] = useState<number | null>(null);
  const detail = useZmenovyListDetail(activeStavbaId, editId);
  const [draft, setDraft] = useState<ZmenovyList | null>(null);
  useEffect(() => {
    if (detail.data) setDraft(detail.data);
  }, [detail.data]);

  async function handleCreate() {
    if (!popisZmeny.trim()) return;
    const iso = czToIso(datumSchvaleni);
    await create.mutateAsync({
      popis_zmeny: popisZmeny.trim(),
      duvod_zmeny: duvod.trim(),
      castka_zmena: castka.trim(),
      dopad_naklady: dopadNaklady.trim(),
      dopad_harmonogram: dopadHarmonogram.trim(),
      puvodni_rozsah: puvodniRozsah.trim(),
      schvalil: schvalil.trim(),
      datum_schvaleni: iso ?? undefined,
    });
    setOpenNew(false);
    setPopisZmeny('');
    setDuvod('');
    setCastka('');
    setDopadNaklady('');
    setDopadHarmonogram('');
    setPuvodniRozsah('');
    setSchvalil('');
    setDatumSchvaleni('');
  }

  async function handleStavChange(newStav: ZmenovyListStav) {
    if (!draft) return;
    await update.mutateAsync({ zlId: draft.id, payload: { stav: newStav } });
  }

  async function handleSaveEdit() {
    if (!draft) return;
    await update.mutateAsync({
      zlId: draft.id,
      payload: {
        puvodni_rozsah: draft.puvodni_rozsah,
        popis_zmeny: draft.popis_zmeny,
        duvod_zmeny: draft.duvod_zmeny,
        dopad_naklady: draft.dopad_naklady,
        castka_zmena: draft.castka_zmena,
        dopad_harmonogram: draft.dopad_harmonogram,
        schvalil: draft.schvalil,
        datum_schvaleni: draft.datum_schvaleni ?? undefined,
        poznamka: draft.poznamka,
      },
    });
  }

  async function handleDelete() {
    if (!draft) return;
    if (!window.confirm(`Opravdu smazat ZL-${String(draft.cislo).padStart(2, '0')}?`)) return;
    await del.mutateAsync(draft.id);
    setEditId(null);
  }

  const columns: ListColumn<ZmenovyList>[] = [
    { key: 'cislo', label: 'Č.', width: '80px', mono: true, dim: true, render: (z) => `ZL-${String(z.cislo).padStart(2, '0')}` },
    { key: 'popis_zmeny', label: 'Popis změny', primary: true, render: (z) => z.popis_zmeny || '—' },
    { key: 'castka_zmena', label: 'Částka', width: '160px', mono: true, align: 'right', render: (z) => z.castka_zmena || '—' },
    { key: 'datum_schvaleni', label: 'Datum', width: '120px', mono: true, dim: true, render: (z) => formatDate(z.datum_schvaleni ?? z.updated_at) },
    {
      key: 'stav',
      label: 'Stav',
      width: '140px',
      render: (z) => {
        const m = stavMap[z.stav] ?? { tone: 'neutral' as const, label: z.stav };
        return <Badge tone={m.tone} pill dot>{m.label}</Badge>;
      },
    },
  ];

  if (!activeStavbaId) {
    return (
      <ListPage breadcrumb={['Realizace', 'Změnové listy']} title="Změnové listy" subtitle="Vyberte nebo založte stavbu">
        <StavbaGate message="Změnové listy se evidují k jedné stavbě." />
      </ListPage>
    );
  }

  return (
    <>
      <ListPage
        breadcrumb={['Realizace', 'Změnové listy']}
        title="Změnové listy"
        subtitle={isLoading ? 'Načítám…' : error ? 'Chyba při načítání' : `${rows.length} listů · vícepráce, méněpráce a záměny oproti smlouvě`}
        status={isLoading ? 'neutral' : error ? 'draft' : 'done'}
        statusText={isLoading ? 'Načítám…' : error ? 'Chyba' : `${rows.length} listů`}
        actions={<Button variant="primary" onClick={() => setOpenNew(true)}>+ Nový změnový list</Button>}
        searchPlaceholder="Hledat změnu…"
      >
        {error ? (
          <div style={{ padding: 24, borderRadius: 4, background: 'var(--sd-danger-soft)', color: 'var(--sd-danger)' }}>
            Chyba: {error instanceof Error ? error.message : 'neznámá'}
          </div>
        ) : (
          <ListTable
            columns={columns}
            rows={rows}
            rowKey={(z) => z.id}
            onRowClick={(z) => setEditId(z.id)}
            emptyTitle="Žádné změnové listy"
            emptyText="Zatím nebyla evidována žádná změna oproti smlouvě."
          />
        )}
      </ListPage>

      {/* Vytvořit */}
      <Modal
        open={openNew}
        onClose={() => setOpenNew(false)}
        title="Nový změnový list"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpenNew(false)}>Zrušit</Button>
            <Button variant="primary" onClick={handleCreate} disabled={create.isPending || !popisZmeny.trim()}>
              {create.isPending ? 'Ukládám…' : 'Vytvořit'}
            </Button>
          </>
        }
      >
        <div style={{ border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', overflow: 'hidden' }}>
          <DocBlock idx="01" title="Popis změny">
            <DocRow k="Popis změny *" multiline value={popisZmeny} onChange={setPopisZmeny} placeholder="Vícepráce: navýšení krovu o 2 vaznice…" />
            <DocRow k="Důvod změny" multiline value={duvod} onChange={setDuvod} placeholder="Statické posouzení požaduje…" />
            <DocRow k="Původní rozsah" multiline value={puvodniRozsah} onChange={setPuvodniRozsah} placeholder="Co bylo původně sjednáno…" />
            <DocRow k="Dopad na cenu" mono value={castka} onChange={setCastka} placeholder="+48 000 Kč bez DPH" />
            <DocRow k="Dopad na náklady — detail" multiline value={dopadNaklady} onChange={setDopadNaklady} placeholder="Materiál, práce, …" />
            <DocRow k="Dopad na harmonogram" value={dopadHarmonogram} onChange={setDopadHarmonogram} placeholder="+3 pracovní dny" />
            <DocRow k="Schválil" value={schvalil} onChange={setSchvalil} placeholder="Jméno schvalovatele (investor / TDS)" />
            <DocRow k="Datum schválení" mono value={datumSchvaleni} onChange={setDatumSchvaleni} placeholder="dd. mm. rrrr" actions={<DocInlineDate value={datumSchvaleni} onChange={setDatumSchvaleni} />} />
          </DocBlock>
        </div>
        {create.isError && (
          <div style={{ marginTop: 12, padding: 12, borderRadius: 4, background: 'var(--sd-danger-soft)', color: 'var(--sd-danger)', fontSize: 13 }}>
            Uložení selhalo: {create.error instanceof Error ? create.error.message : 'neznámá chyba'}
          </div>
        )}
      </Modal>

      {/* Detail/Edit */}
      <Modal
        open={!!editId}
        onClose={() => setEditId(null)}
        title={draft ? `ZL-${String(draft.cislo).padStart(2, '0')}` : 'Změnový list'}
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditId(null)}>Zavřít</Button>
            <Button variant="danger" onClick={handleDelete} disabled={del.isPending || !draft}>
              {del.isPending ? 'Mažu…' : '🗑️ Smazat'}
            </Button>
            <Button variant="primary" onClick={handleSaveEdit} disabled={update.isPending || !draft}>
              {update.isPending ? 'Ukládám…' : 'Uložit změny'}
            </Button>
          </>
        }
      >
        {detail.isLoading || !draft ? (
          <div style={{ padding: 20, color: 'var(--sd-doc-dim)' }}>Načítám…</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--sd-text-dim)', textTransform: 'uppercase' }}>Stav:</span>
              {(['navrh', 'ke_schvaleni', 'schvaleno', 'zamitnuto'] as ZmenovyListStav[]).map((st) => (
                <Button
                  key={st}
                  size="sm"
                  variant={draft.stav === st ? 'primary' : 'secondary'}
                  onClick={() => handleStavChange(st)}
                  disabled={update.isPending || draft.stav === st}
                >
                  {stavMap[st].label}
                </Button>
              ))}
            </div>

            <div style={{ border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', overflow: 'hidden' }}>
              <DocBlock idx="01" title="Popis změny">
                <DocRow k="Popis změny" multiline rows={3} value={draft.popis_zmeny} onChange={(v) => setDraft({ ...draft, popis_zmeny: v })} />
                <DocRow k="Důvod změny" multiline rows={3} value={draft.duvod_zmeny} onChange={(v) => setDraft({ ...draft, duvod_zmeny: v })} />
                <DocRow k="Původní rozsah" multiline rows={2} value={draft.puvodni_rozsah} onChange={(v) => setDraft({ ...draft, puvodni_rozsah: v })} />
              </DocBlock>
            </div>

            <div style={{ border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', overflow: 'hidden' }}>
              <DocBlock idx="02" title="Dopady">
                <DocRow k="Cena (souhrn)" mono value={draft.castka_zmena} onChange={(v) => setDraft({ ...draft, castka_zmena: v })} placeholder="+48 000 Kč bez DPH" />
                <DocRow k="Dopad — náklady (detail)" multiline rows={3} value={draft.dopad_naklady} onChange={(v) => setDraft({ ...draft, dopad_naklady: v })} />
                <DocRow k="Dopad — harmonogram" value={draft.dopad_harmonogram} onChange={(v) => setDraft({ ...draft, dopad_harmonogram: v })} placeholder="+3 pracovní dny" />
              </DocBlock>
            </div>

            <div style={{ border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', overflow: 'hidden' }}>
              <DocBlock idx="03" title="Schválení">
                <DocRow k="Schválil" value={draft.schvalil} onChange={(v) => setDraft({ ...draft, schvalil: v })} placeholder="Jméno schvalovatele" />
                <DocRow
                  k="Datum schválení"
                  mono
                  value={isoToCz(draft.datum_schvaleni)}
                  onChange={(v) => setDraft({ ...draft, datum_schvaleni: czToIso(v) })}
                  placeholder="dd. mm. rrrr"
                  actions={
                    <DocInlineDate
                      value={isoToCz(draft.datum_schvaleni)}
                      onChange={(v) => setDraft({ ...draft, datum_schvaleni: czToIso(v) })}
                    />
                  }
                />
                <DocRow k="Poznámka" multiline rows={2} value={draft.poznamka} onChange={(v) => setDraft({ ...draft, poznamka: v })} />
              </DocBlock>
            </div>

            {update.isError && (
              <div style={{ padding: 10, borderRadius: 4, background: 'var(--sd-danger-soft)', color: 'var(--sd-danger)', fontSize: 12.5 }}>
                Chyba: {update.error instanceof Error ? update.error.message : 'neznámá'}
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
